import React from 'react';
import { useFormik, FormikConfig, FormikValues, FormikHelpers } from 'formik';
import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  TextField,
  MenuItem,
  useTheme,
  CircularProgress,
  Paper,
  Typography,
  InputLabel,
  Select,
  FormControlLabel,
  Checkbox,
  RadioGroup,
  Radio,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { FileUpload } from '../FileUpload/FileUpload';

export interface FormField {
  name: string;
  label: string;
  type: 'text' | 'select' | 'date' | 'time' | 'file' | 'textarea' | 'number' | 'email' | 'tel' | 'url' | 'checkbox' | 'radio';
  options?: { value: string | number; label: string }[];
  required?: boolean;
  fullWidth?: boolean;
  multiline?: boolean;
  rows?: number;
  accept?: string | Record<string, string[]>;
}

interface FormProps<T extends FormikValues> {
  fields: FormField[];
  initialValues: T;
  validationSchema?: any;
  onSubmit: (values: T, helpers: FormikHelpers<T>) => Promise<void>;
  submitButtonText?: string;
  loading?: boolean;
  title?: string;
  onCancel?: () => void;
}

export function Form<T extends FormikValues>({
  fields,
  initialValues,
  validationSchema,
  onSubmit,
  submitButtonText = 'Speichern',
  loading = false,
  title,
  onCancel,
}: FormProps<T>) {
  const theme = useTheme();

  const formik = useFormik<T>({
    initialValues,
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values: T, helpers: FormikHelpers<T>) => {
      try {
        await onSubmit(values, helpers);
      } catch (error) {
        console.error('Form submission error:', error);
        helpers.setSubmitting(false);
      }
    },
  });

  const renderField = (field: FormField) => {
    const commonProps = {
      fullWidth: field.fullWidth !== false,
      name: field.name,
      label: field.label,
      value: formik.values[field.name],
      onChange: formik.handleChange,
      onBlur: formik.handleBlur,
      error: formik.touched[field.name] && Boolean(formik.errors[field.name]),
      helperText: formik.touched[field.name] && formik.errors[field.name] as string,
      required: field.required,
    };

    switch (field.type) {
      case 'text':
      case 'email':
      case 'tel':
      case 'url':
        return (
          <TextField
            {...commonProps}
            type={field.type}
            multiline={field.multiline}
            rows={field.rows}
          />
        );
      case 'select':
        return (
          <TextField
            {...commonProps}
            select
          >
            {field.options?.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        );
      case 'checkbox':
        return (
          <FormControlLabel
            control={
              <Checkbox
                name={field.name}
                checked={Boolean(formik.values[field.name])}
                onChange={formik.handleChange}
              />
            }
            label={field.label}
          />
        );
      case 'radio':
        return (
          <FormControl component="fieldset">
            <Typography variant="subtitle1" gutterBottom>
              {field.label}
            </Typography>
            <RadioGroup
              name={field.name}
              value={formik.values[field.name]}
              onChange={formik.handleChange}
            >
              {field.options?.map((option) => (
                <FormControlLabel
                  key={option.value}
                  value={option.value}
                  control={<Radio />}
                  label={option.label}
                />
              ))}
            </RadioGroup>
          </FormControl>
        );
      case 'date':
        return (
          <DatePicker
            label={field.label}
            value={formik.values[field.name]}
            onChange={(date) => formik.setFieldValue(field.name, date)}
            slotProps={{
              textField: {
                fullWidth: true,
                required: field.required,
                name: field.name,
                error: formik.touched[field.name] && Boolean(formik.errors[field.name]),
                helperText: formik.touched[field.name] && formik.errors[field.name] as string,
              },
            }}
          />
        );
      case 'time':
        return (
          <TimePicker
            label={field.label}
            value={formik.values[field.name]}
            onChange={(time) => formik.setFieldValue(field.name, time)}
            slotProps={{
              textField: {
                fullWidth: true,
                required: field.required,
                name: field.name,
                error: formik.touched[field.name] && Boolean(formik.errors[field.name]),
                helperText: formik.touched[field.name] && formik.errors[field.name] as string,
              },
            }}
          />
        );
      case 'file':
        return (
          <FileUpload
            files={formik.values[field.name] || []}
            onFilesChange={(files) => formik.setFieldValue(field.name, files)}
            accept={field.accept}
            label={field.label}
          />
        );
      case 'textarea':
        return (
          <TextField
            {...commonProps}
            multiline
            rows={field.rows || 4}
          />
        );
      case 'number':
        return (
          <TextField
            {...commonProps}
            type="number"
          />
        );
      default:
        return (
          <TextField
            {...commonProps}
            multiline={field.multiline}
            rows={field.rows}
          />
        );
    }
  };

  return (
    <Paper elevation={3} sx={{ p: 3 }}>
      {title && (
        <Typography variant="h5" gutterBottom>
          {title}
        </Typography>
      )}
      <Box component="form" onSubmit={formik.handleSubmit}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 2 }}>
          {fields.map((field) => (
            <Box key={field.name} sx={field.type === 'checkbox' || field.type === 'radio' || field.type === 'textarea' ? { gridColumn: '1 / -1' } : {}}>
              {renderField(field)}
            </Box>
          ))}
        </Box>
        <Box sx={{ display: 'flex', gap: 2, mt: 3, justifyContent: 'flex-end' }}>
          {onCancel && (
            <Button variant="outlined" onClick={onCancel}>
              Abbrechen
            </Button>
          )}
          <Button
            type="submit"
            variant="contained"
            disabled={formik.isSubmitting || loading}
            startIcon={loading && <CircularProgress size={20} color="inherit" />}
          >
            {submitButtonText}
          </Button>
        </Box>
      </Box>
    </Paper>
  );
} 